/***
 *
 *   PROJECT AUTO-DELETE (settings)
 *   Dedicated card for completed-ticket cleanup. Kept out of the General form
 *   so identity/branding stay separate from this irreversible lifecycle rule.
 *
 *   Layout: switch → nested delay when on → outcome summary that mirrors
 *   the delay (immediate / 1 day / N days). Own save so General edits
 *   aren't coupled to this destructive preference.
 *
 **********/

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { Animate, Card, Button, Switch, useProjectContext } from 'components/lib';
import { useTranslate } from 'app/translations';
import { useApiContext } from 'app/api';
import Style from './autoDelete.tailwind.js';

const DEFAULT_DAYS = 30;
const MIN_DAYS = 0;
const MAX_DAYS = 365;

function clampDays(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return DEFAULT_DAYS;
    return Math.min(MAX_DAYS, Math.max(MIN_DAYS, Math.trunc(n)));
}

function summaryKey(days) {
    if (days === 0) return 'project.autoDeleteCompleted.summaryImmediate';
    if (days === 1) return 'project.autoDeleteCompleted.summaryOne';
    return 'project.autoDeleteCompleted.summary';
}

export default function ProjectAutoDelete() {
    const { id } = useParams();
    const { _t } = useTranslate();
    const { callApi } = useApiContext();
    const queryClient = useQueryClient();
    const { projects } = useProjectContext();
    const project = projects?.find((p) => p.id === id);

    const [enabled, setEnabled] = useState(() =>
        Boolean(project?.auto_delete_completed_tickets),
    );
    const [days, setDays] = useState(() =>
        clampDays(project?.auto_delete_completed_after_days ?? DEFAULT_DAYS),
    );
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [savedFlash, setSavedFlash] = useState(false);

    useEffect(() => {
        if (!project) return;
        setEnabled(Boolean(project.auto_delete_completed_tickets));
        setDays(clampDays(project.auto_delete_completed_after_days ?? DEFAULT_DAYS));
        setError(null);
        setSavedFlash(false);
    }, [
        project?.id,
        project?.auto_delete_completed_tickets,
        project?.auto_delete_completed_after_days,
    ]);

    if (!project) return null;

    const serverEnabled = Boolean(project.auto_delete_completed_tickets);
    const serverDays = clampDays(project.auto_delete_completed_after_days ?? DEFAULT_DAYS);
    const daysEmpty = days === '';
    const effectiveDays = clampDays(daysEmpty ? DEFAULT_DAYS : days);
    const dirty =
        enabled !== serverEnabled || (enabled && !daysEmpty && effectiveDays !== serverDays);
    const canSave = dirty && !saving && !(enabled && daysEmpty);

    async function save() {
        if (!canSave) return;
        setSaving(true);
        setError(null);
        try {
            const res = await callApi({
                method: 'patch',
                url: `/api/project/${id}`,
                data: {
                    auto_delete_completed_tickets: enabled,
                    auto_delete_completed_after_days: effectiveDays,
                },
            });
            if (res.status < 200 || res.status >= 300) {
                setError(res.message || _t('error.tryAgain'));
                return;
            }
            await queryClient.invalidateQueries('projects');
            setSavedFlash(true);
            window.setTimeout(() => setSavedFlash(false), 2000);
        } catch (err) {
            setError(err?.message || _t('error.tryAgain'));
        } finally {
            setSaving(false);
        }
    }

    function onDaysChange(e) {
        const raw = e.target.value;
        setSavedFlash(false);
        if (raw === '') {
            setDays('');
            return;
        }
        setDays(clampDays(raw));
    }

    function onDaysBlur() {
        setDays(clampDays(daysEmpty ? DEFAULT_DAYS : days));
    }

    return (
        <Animate>
            <Card
                title={_t('project.autoDeleteCompleted.sectionTitle')}
                description={_t('project.autoDeleteCompleted.sectionDescription')}
                className="w-full">
                <div className={enabled ? Style.panelOn : Style.panel}>
                    <Switch
                        name="auto_delete_completed_tickets"
                        label={_t('project.autoDeleteCompleted.label')}
                        hint={_t('project.autoDeleteCompleted.hint')}
                        checked={enabled}
                        onChange={(_name, value) => {
                            setEnabled(Boolean(value));
                            setSavedFlash(false);
                            setError(null);
                            if (value && daysEmpty) {
                                setDays(serverDays || DEFAULT_DAYS);
                            }
                        }}
                    />

                    {enabled ? (
                        <div className={Style.delayBlock}>
                            <label className={Style.delayLabel} htmlFor="auto-delete-days">
                                {_t('project.autoDeleteCompleted.afterDaysLabel')}
                            </label>
                            <div className={Style.delayRow}>
                                <input
                                    id="auto-delete-days"
                                    type="number"
                                    min={MIN_DAYS}
                                    max={MAX_DAYS}
                                    inputMode="numeric"
                                    className={Style.daysInput}
                                    value={days}
                                    onChange={onDaysChange}
                                    onBlur={onDaysBlur}
                                    aria-describedby="auto-delete-summary auto-delete-hint"
                                />
                                <span className={Style.daysSuffix}>
                                    {_t('project.autoDeleteCompleted.daysUnit')}
                                </span>
                            </div>
                            <p id="auto-delete-summary" className={Style.summary}>
                                {_t(summaryKey(effectiveDays), { days: effectiveDays })}
                            </p>
                            <p id="auto-delete-hint" className={Style.hint}>
                                {_t('project.autoDeleteCompleted.afterDaysHint')}
                            </p>
                        </div>
                    ) : (
                        <p className={Style.offHint}>
                            {_t('project.autoDeleteCompleted.offHint')}
                        </p>
                    )}
                </div>

                {error && <p className={Style.error}>{error}</p>}

                <div className={Style.footer}>
                    <Button
                        variant="primary"
                        size="sm"
                        text={
                            savedFlash
                                ? _t('project.autoDeleteCompleted.saved')
                                : _t('save')
                        }
                        action={save}
                        isLoading={saving}
                        disabled={!canSave}
                    />
                </div>
            </Card>
        </Animate>
    );
}
